// Листинги (6.1): команды, лента, вставка кода из IDE и файлов, преобразование абзацев в листинг, живые правила.
import type { Ctx, RegistryApi, LintIssue } from '../../../core/registry';
import type { JSONContent } from '@tiptap/core';
import { insertBlocks, focusInside } from '../../../core/editor/insert';
import { newId } from '../../../core/schema/util';
import { pickFiles, readTextFile, ancestor } from '../util';
import { ListingView } from './ListingView';
import { CODE_EXT, CODE_LINE_RE, detectLang, fixSyntaxChars, langById, langFromFilename, normalizeCode, oddIndentLines, syntaxIssues } from './langs';
import { focusCode } from './views';

const CODE_ACCEPT = [...CODE_EXT].map((e) => '.' + e).join(',') + ',.txt';

function listingJson(code: string, lang: string, id = newId()): JSONContent {
  return { type: 'listing', attrs: { id, lang: langById(lang).id, code }, content: [{ type: 'caption' }] };
}

/** Вставить листинг(и); курсор - в название первого. */
export function insertListings(c: Ctx, items: { code: string; lang: string }[], at?: number | null) {
  const ed = c.editor;
  if (!ed || !items.length) return;
  const pos = insertBlocks(ed, items.map((x) => listingJson(x.code, x.lang)), at ?? undefined);
  focusInside(ed, pos);
}

/** Выделенные абзацы -> листинг (строки = абзацы). */
function selectionToListing(c: Ctx): boolean {
  const ed = c.editor!;
  const { from, to, empty } = ed.state.selection;
  if (empty) return false;
  const $a = ed.state.doc.resolve(from); const $b = ed.state.doc.resolve(to);
  if ($a.depth < 1 || $b.depth < 1) return false;
  const start = $a.before(1); const end = $b.after(1);
  const lines: string[] = [];
  let ok = true;
  ed.state.doc.nodesBetween(start, end, (n, _p, parent) => {
    if (parent?.type.name !== 'doc' && parent?.type.name !== 'appendix') return true;
    if (n.type.name === 'paragraph') lines.push(textOf(n));
    else ok = false;
    return false;
  });
  if (!ok || !lines.length) return false;
  const code = normalizeCode(lines.join('\n'));
  const id = newId();
  ed.chain().focus().insertContentAt({ from: start, to: end }, listingJson(fixSyntaxChars(code), detectLang(code).id, id)).run();
  focusInside(ed, start);
  return true;
}

function textOf(n: any): string {
  let s = '';
  n.forEach((ch: any) => { s += ch.isText ? ch.text : ch.type.name === 'hardBreak' ? '\n' : ''; });
  return s;
}

export function setupCode(r: RegistryApi) {
  // ---------------------------------------------------------------------------------------- команды
  r.commands.add({ id: 'insert.listing', title: 'Листинг', icon: 'braces', shortcut: 'Mod-Alt-l',
    description: 'Программный код с названием «Листинг N. …» (6.1). Быстро: «```» в начале строки; код из IDE можно просто вставить',
    run: (c) => {
      if (!c.editor) return;
      if (selectionToListing(c)) return;
      insertListings(c, [{ code: '', lang: '' }]);
    } });
  r.commands.add({ id: 'listing.fromFile', title: 'Листинг из файла…', icon: 'tfc-code-file', description: 'Исходный код из файла (.py, .c, .m, .java …)',
    run: async (c) => {
      const files = await pickFiles(CODE_ACCEPT);
      const items: { code: string; lang: string }[] = [];
      for (const f of files) items.push(await fileItem(f));
      insertListings(c, items);
    } });
  r.commands.add({ id: 'listing.loadFile', title: 'Загрузить код из файла…', icon: 'tfc-code-file',
    isEnabled: (c) => !!c.editor && !!ancestor(c.editor, 'listing'),
    run: async (c) => {
      const l = c.editor && ancestor(c.editor, 'listing');
      if (!l) return;
      const [f] = await pickFiles(CODE_ACCEPT, false);
      if (!f) return;
      const it = await fileItem(f);
      const cur = ancestor(c.editor!, 'listing') || l;
      c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(cur.pos, undefined, { ...cur.node.attrs, code: it.code, lang: it.lang || cur.node.attrs.lang }));
    } });
  r.commands.add({ id: 'listing.fixChars', title: 'Исправить кавычки и тире в коде', icon: 'tfc-magic',
    isEnabled: (c) => !!c.editor && !!ancestor(c.editor, 'listing'),
    run: (c) => {
      const l = ancestor(c.editor!, 'listing');
      if (l) c.editor!.view.dispatch(c.editor!.state.tr.setNodeMarkup(l.pos, undefined, { ...l.node.attrs, code: fixSyntaxChars(l.node.attrs.code || '') }));
    } });
  r.commands.add({ id: 'listing.edit', title: 'Перейти в код листинга', icon: 'braces', isEnabled: (c) => !!c.editor && !!ancestor(c.editor, 'listing'),
    run: (c) => { const l = ancestor(c.editor!, 'listing'); if (l) focusCode(l.node.attrs.id, 'end'); } });
  r.commands.add({ id: 'listing.fromSelection', title: 'Оформить выделенное как листинг', icon: 'braces',
    run: (c) => { if (c.editor && !selectionToListing(c)) c.toast('Выделите абзацы с кодом', 'info'); } });

  // ---------------------------------------------------------------------------------------- лента
  r.ribbonGroups.add({ id: 'insert.code', tab: 'insert', title: 'Код', order: 20 });
  r.ribbonItems.add({ id: 'listing', group: 'insert.code', type: 'split', command: 'insert.listing', icon: 'braces', order: 0,
    items: [
      { command: 'insert.listing', label: 'Пустой листинг' },
      { command: 'listing.fromFile' },
      { command: 'listing.fromSelection' },
      { separator: true },
      { label: 'Подсказка: «```» в начале строки или вставка кода из IDE - сразу листинг', icon: 'info', disabled: true },
    ] });

  r.contextMenu.add({ id: 'tfc.listing', order: 12, items: (c, target) => {
    if (target.nodes.some((n) => n.type === 'listing')) return [{ command: 'listing.edit' }, { command: 'listing.loadFile' }, { command: 'listing.fixChars' }];
    if (!target.selectionEmpty && c.editor) {
      const txt = c.editor.state.doc.textBetween(c.editor.state.selection.from, c.editor.state.selection.to, '\n');
      if (txt.includes('\n') && detectLang(txt).id) return [{ command: 'listing.fromSelection' }];
    }
    return null;
  } });

  r.nodeViews.add({ id: 'listing', component: ListingView });

  // ---------------------------------------------------------------------------------------- вставка
  // код из IDE (VS Code, JetBrains, Visual Studio - HTML с <pre>/моноширинным шрифтом) и многострочный текст,
  // уверенно похожий на код (не при Ctrl+Shift+V) - листингом с сохранением отступов
  r.paste.add({ id: 'tfc.codePaste', priority: 90,
    match: (d, c) => {
      if (d.kind !== 'paste' || d.files.length || !d.text.includes('\n')) return false;
      // формулы (MathML, LaTeX) и документы Word/Excel - дело других функций
      if (/<(mml:)?math[\s>]|\$\$|\\begin\{(equation|align|gather|multline)|\\\[|urn:schemas-microsoft-com:office:(word|excel)/.test(d.text + d.html)) return false;
      const ed = c.editor;
      if (!ed || (ed.view as any).input?.shiftKey) return false;
      const $f = ed.state.selection.$from;
      for (let k = $f.depth; k > 0; k--) if (['tableCell', 'tableHeader', 'caption', 'formula'].includes($f.node(k).type.name)) return false;
      if (d.types.includes('vscode-editor-data')) return true;
      if (isIdeHtml(d.html)) return true;
      return !d.html && detectLang(d.text).score >= 3 && d.text.split('\n').filter((l) => CODE_LINE_RE.test(l) || /^\s{2,}\S/.test(l)).length >= 2;
    },
    handle: (d, c) => {
      let lang = '';
      try { lang = JSON.parse(d.event instanceof ClipboardEvent ? d.event.clipboardData?.getData('vscode-editor-data') || '{}' : '{}').mode || ''; } catch { /* нет данных VS Code */ }
      let code = normalizeCode(d.text);
      if (syntaxIssues(code).quotes) code = fixSyntaxChars(code);
      if (!lang || langById(lang).id === '' ) lang = detectLang(code).id;
      insertListings(c, [{ code, lang }]);
      c.toast(`Код вставлен листингом${lang ? ` (${langById(lang).title})` : ''} - введите название. Как обычный текст: Ctrl+Shift+V`, 'ok', 4000);
      return true;
    } });
  // файлы с кодом (перетаскивание, вставка файла)
  r.paste.add({ id: 'tfc.codeFiles', priority: 96,
    match: (d) => d.files.length > 0 && d.files.every((f) => CODE_EXT.has((f.name.match(/\.([^.]+)$/) || [])[1]?.toLowerCase() || '')),
    handle: async (d, c) => {
      const items: { code: string; lang: string }[] = [];
      for (const f of d.files) items.push(await fileItem(f));
      let at: number | null = null;
      if (d.pos !== null && c.editor) { const $p = c.editor.state.doc.resolve(d.pos); at = $p.depth ? $p.after(1) : d.pos; }
      insertListings(c, items, at);
      c.toast(items.length > 1 ? `Вставлено листингов: ${items.length}` : 'Код из файла вставлен листингом - введите название', 'ok', 3000);
    } });

  // ---------------------------------------------------------------------------------------- живые правила
  r.lint.add({ id: 'tfc.listings', title: 'Листинги', run: (ix) => {
    const out: LintIssue[] = [];
    for (const e of ix.entries) {
      if (e.type !== 'listing') continue;
      const code: string = e.attrs.code || '';
      const label = e.num?.label || 'Листинг';
      if (!code.trim()) { out.push({ sev: 'W', node: e.id, msg: `${label}: пустой - вставьте код` }); continue; }
      const iss = syntaxIssues(code);
      if (iss.quotes || iss.other.length) out.push({ sev: 'E', node: e.id, rule: '6.1.1',
        msg: `${label}: в синтаксисе кода только ASCII - типографские ${iss.quotes ? 'кавычки' : iss.other.join(' ')}`,
        fix: { label: 'Заменить на ASCII', run: (c) => fixListing(c, e.id) } });
      if (code.includes('\t')) out.push({ sev: 'E', node: e.id, rule: '6.1.2', msg: `${label}: табуляции в коде не допускаются`,
        fix: { label: 'Заменить на 4 пробела', run: (c) => fixListing(c, e.id) } });
      const odd = oddIndentLines(code);
      if (odd) out.push({ sev: 'W', node: e.id, rule: '6.1.2', msg: `${label}: отступы кратны 2 или 4 пробелам (нечётных строк: ${odd})` });
      const n = code.split('\n').filter((l) => l.trim()).length;
      if (n > 50 && !e.appendix) out.push({ sev: 'W', node: e.id, rule: '6.1.4', msg: `${label}: объёмный (${n} строк) - выносите в приложение, в тексте оставьте фрагмент` });
      const cap = e.node.firstChild?.textContent.trim() || '';
      if (/^листинг\s*\d/i.test(cap)) out.push({ sev: 'W', node: e.id, rule: '6.1.5', msg: `${label}: «Листинг N.» ставится автоматически - оставьте только название` });
    }
    // код обычным текстом (CODE-AS-TEXT): 2+ абзаца подряд, похожих на код
    let run: string[] = [];
    const flush = () => {
      if (run.length >= 2) {
        const ids = run;
        out.push({ sev: 'W', node: ids[0], rule: '6.1', msg: 'Похоже на программный код обычным текстом - оформите листингом',
          fix: { label: 'Оформить листингом', run: (c) => codeRunToListing(c, ids) } });
      }
      run = [];
    };
    ix.doc.forEach((n) => {
      if (n.type.name === 'paragraph' && CODE_LINE_RE.test(n.textContent)) run.push(n.attrs.id); else flush();
    });
    flush();
    return out;
  } });
}

function isIdeHtml(html: string): boolean {
  if (!html) return false;
  if (/<pre[\s>]/i.test(html) && !/<(p|table|h\d|li)[\s>]/i.test(html)) return true;       // JetBrains, браузер (блок кода)
  // Visual Studio / VS Code-подобные: div со white-space: pre и моноширинным шрифтом
  return /white-space:\s*pre/i.test(html) && /font-family:[^;"]*(consolas|courier|mono|menlo|cascadia)/i.test(html) && !/<(p|table|h\d|li)[\s>]/i.test(html);
}

async function fileItem(f: File): Promise<{ code: string; lang: string }> {
  const text = await readTextFile(f);
  const code = normalizeCode(text);
  return { code, lang: langFromFilename(f.name) ?? detectLang(code).id };
}

function fixListing(c: Ctx, id: string) {
  const ed = c.editor;
  if (!ed) return;
  ed.state.doc.descendants((n, pos) => {
    if (n.attrs?.id !== id) return true;
    ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, code: fixSyntaxChars(normalizeCode(n.attrs.code || '')) }));
    return false;
  });
}

function codeRunToListing(c: Ctx, ids: string[]) {
  const ed = c.editor;
  if (!ed || !ids.length) return;
  let from = -1; let to = -1;
  ed.state.doc.forEach((n, off) => {
    if (ids.includes(n.attrs.id)) { if (from < 0) from = off; to = off + n.nodeSize; }
  });
  if (from < 0) return;
  ed.chain().setTextSelection({ from: from + 1, to: to - 1 }).run();
  selectionToListing(c);
}
